import React from "react";
import { Button, Input } from "antd";
import { ColumnType } from "antd/lib/table";
import classnames from "classnames";
import { compact, isNil, map, startCase, trim } from "lodash";
import { EMPTY } from "constants/common";
import { AICC_TTS_TARGET, AiccTtsTarget, AiccTtsVoice, AiccTtsVoiceRow } from "types/model/aiccLearningAssets";
import {
  isVoiceIdNotFound,
  showBaseSpeed,
  TTS_BASE_SPEED_REQUIRED,
  TTS_SAME_VOICE_ERROR,
  TTS_TARGET_LABEL,
  TTS_VOICE_ID_NOT_FOUND,
  TTS_VOICE_ID_REQUIRED,
  VoiceResolutions,
  voiceNameOf,
  VoicesById,
} from "./configs";
import learningAssetStyles from "../LearningAssets.module.scss";
import styles from "./TtsSetting.module.scss";

interface ColumnHandlers {
  isEditing: boolean;
  showErrors: boolean;
  voicesById: VoicesById;
  resolutions: VoiceResolutions;
  onVoiceIdChange: (gender: string, target: AiccTtsTarget, voiceId: string) => void;
  onAdjust: (gender: string, target: AiccTtsTarget) => void;
}

const getVoiceColumn = (
  target: AiccTtsTarget,
  { isEditing, showErrors, voicesById, resolutions, onVoiceIdChange, onAdjust }: ColumnHandlers
): ColumnType<AiccTtsVoiceRow> => ({
  title: TTS_TARGET_LABEL[target],
  dataIndex: target,
  key: target,
  width: 360,
  className: styles.voiceColumn,
  render: (voice: AiccTtsVoice, row) => {
    const isVoiceIdMissing = showErrors && !trim(voice.voiceId);
    const isBaseSpeedMissing = showErrors && isNil(voice.baseSpeed);
    const isSameVoice = showErrors && !!trim(voice.voiceId) && trim(row.main.voiceId) === trim(row.fallback.voiceId);
    // Shown as soon as the vendor answers rather than on Save, so a bad paste is caught while it is typed.
    const isVoiceNotFound = isEditing && isVoiceIdNotFound(voice.voiceId, voicesById, resolutions);
    const errors = compact([
      isVoiceIdMissing && TTS_VOICE_ID_REQUIRED,
      isVoiceNotFound && TTS_VOICE_ID_NOT_FOUND,
      isSameVoice && TTS_SAME_VOICE_ERROR,
      isBaseSpeedMissing && TTS_BASE_SPEED_REQUIRED,
    ]);
    const name = voiceNameOf(voice.voiceId, voicesById, resolutions);

    return (
      <div className={learningAssetStyles.stackedCell}>
        {isEditing ? (
          <div className={styles.voiceIdEditor}>
            <span className={styles.cellLabel}>Voice ID: </span>
            <Input
              className={classnames(styles.voiceIdInput, {
                [learningAssetStyles.inputError]: isVoiceIdMissing || isVoiceNotFound || isSameVoice,
              })}
              value={voice.voiceId}
              placeholder="Voice ID"
              onChange={(event) => onVoiceIdChange(row.id, target, event.target.value)}
            />
          </div>
        ) : (
          <div className={styles.voiceId}>
            <span className={styles.cellLabel}>Voice ID: </span>
            {voice.voiceId || EMPTY}
          </div>
        )}
        <div className={styles.voiceName}>
          <span className={styles.cellLabel}>Name: </span>
          {name}
        </div>
        <div className={styles.baseSpeed}>
          <span>
            <span className={styles.cellLabel}>Base speed: </span>
            <span className={classnames({ [learningAssetStyles.fieldErrorText]: isBaseSpeedMissing })}>
              {showBaseSpeed(voice.baseSpeed)}
            </span>
          </span>
          {isEditing && !!voice.voiceId && (
            <Button type="link" className={learningAssetStyles.linkButton} onClick={() => onAdjust(row.id, target)}>
              Adjust
            </Button>
          )}
        </div>
        {map(errors, (error) => (
          <div key={error} className={learningAssetStyles.fieldErrorText}>
            {error}
          </div>
        ))}
      </div>
    );
  },
});

const getColumns = (handlers: ColumnHandlers): ColumnType<AiccTtsVoiceRow>[] => [
  {
    title: "Gender",
    dataIndex: "id",
    key: "id",
    width: 160,
    render: (gender: string) => startCase(gender),
  },
  getVoiceColumn(AICC_TTS_TARGET.main, handlers),
  getVoiceColumn(AICC_TTS_TARGET.fallback, handlers),
];

export default getColumns;
